import { useState, useMemo } from 'react';
import { useAdminContact, useMarkAsRead, useDeleteMessage, ContactMessage } from '../../../core/api/hooks/useContact';
import { AdminTable, Column } from '../components/shared/AdminTable';
import { PageHeader } from '../components/shared/PageHeader';
import { Check, Trash2, Eye } from 'lucide-react';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import clsx from 'clsx';
import { format } from 'date-fns/format';
import { useAdminT } from '../../../hooks/useAdminT';

function InboxPage() {
    const { t } = useAdminT();
    const [search] = useState('');
    const { data, isLoading } = useAdminContact({ limit: 1000, search });
    const markAsRead = useMarkAsRead();
    const deleteMessage = useDeleteMessage();

    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [viewMessage, setViewMessage] = useState<ContactMessage | null>(null);

    const columns = useMemo<Column<ContactMessage>[]>(() => [
        {
            header: t("inquiries.fields.name"),
            accessorKey: 'name',
            cell: (item) => (
                <div className="flex flex-col">
                    <span className={clsx("font-medium", !item.read && "text-primary font-bold")}>
                        {item.name}
                    </span>
                    <span className="text-xs text-text-muted">{item.email}</span>
                </div>
            ),
        },
        {
            header: t("inquiries.fields.message"),
            accessorKey: 'message',
            cell: (item) => (
                <p className="truncate max-w-xs text-text-muted" title={item.message}>
                    {item.message}
                </p>
            ),
        },
        {
            header: t("common.createdAt"),
            accessorKey: 'createdAt',
            cell: (item) => <span className="text-sm text-text-muted">{format(new Date(item.createdAt), 'MMM dd, yyyy HH:mm')}</span>,
        },
    ], [t]);

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">
            <PageHeader
                title={t("inbox.title")}
                description={t("inbox.subtitle")}
            />

            <div className="bg-white rounded-xl shadow-sm border border-accent/10 overflow-hidden">
                <AdminTable
                    data={data?.items || []}
                    columns={columns}
                    isLoading={isLoading}
                    actions={(item) => (
                        <>
                            <button
                                onClick={() => {
                                    setViewMessage(item);
                                    if (!item.read) {
                                        markAsRead.mutate(item.id);
                                    }
                                }}
                                className="p-2 text-slate-400 hover:text-primary hover:bg-slate-100 rounded-md transition-colors"
                                title={t("inbox.viewMessage")}
                            >
                                <Eye className="w-4 h-4" />
                            </button>
                            {!item.read && (
                                <button
                                    onClick={() => markAsRead.mutate(item.id)}
                                    className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                    title={t("inbox.markAsRead")}
                                >
                                    <Check className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                onClick={() => setDeleteId(item.id)}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                title={t("common.delete")}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </>
                    )}
                />
            </div>

            <ConfirmDialog
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={() => {
                    if (deleteId) {
                        deleteMessage.mutate(deleteId);
                        setDeleteId(null);
                    }
                }}
                title={t("inbox.deleteMessage")}
                message={t("common.confirmProceed")}
                confirmText={t("inbox.deleteMessage")}
                variant="danger"
            />

            {/* View Message Modal */}
            {viewMessage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900">{t("inbox.messageDetails")}</h3>
                            <button onClick={() => setViewMessage(null)} className="text-gray-400 hover:text-gray-600 transition">
                                <span className="sr-only">Close</span>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t("inbox.from")}</label>
                                <div className="text-gray-900 font-medium">{viewMessage.name}</div>
                                <div className="text-primary text-sm">{viewMessage.email}</div>
                                {viewMessage.phone && <div className="text-gray-500 text-sm">{viewMessage.phone}</div>}
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t("common.createdAt")}</label>
                                <div className="text-gray-700 text-sm">{format(new Date(viewMessage.createdAt), 'PPpp')}</div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t("inquiries.fields.message")}</label>
                                <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-wrap">{viewMessage.message}</p>
                            </div>
                        </div>
                        <div className="p-6 pt-0 flex justify-end">
                            <button
                                onClick={() => setViewMessage(null)}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium transition"
                            >
                                {t("common.cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InboxPage;
